import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Skeleton,
  Chip,
  Pagination,
  Stack,
  Switch
} from '@mui/material';
import { FaSearch as SearchIcon } from 'react-icons/fa';
import { MdDeleteForever, MdAdd, MdEdit, MdClose } from "react-icons/md";
import { FiType, FiCircle } from 'react-icons/fi';
import { getData, postData, putData, deleteData } from '../../Webservice/Webservice';
import CATEGORY_ROUTES from '../../API_Route/CategoryRoute';

const PAGE_SIZE = 10;

function CategoryCluster() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');

  // Modal states
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    status: 'active',
    position: ''
  });

  const fetchClusters = async () => {
    setLoading(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await getData(CATEGORY_ROUTES.LIST_CATEGORY_CLUSTER, { 
        page, 
        limit: PAGE_SIZE,
        search: searchTerm,
        ...(filterStatus && { status: filterStatus })
      }, token);
      
      if (response && response.clusters) {
        setClusters(response.clusters);
        setTotalPages(response.meta?.totalPages || 1);
        setTotalItems(response.meta?.total || response.clusters.length);
      } else {
        setClusters([]);
      }
    } catch (error) {
      console.error('Failed to fetch clusters:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClusters();
  }, [page, searchTerm, filterStatus]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleAddSubmit = async () => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await postData('category-cluster/add', formData, token);
      setOpenAddModal(false);
      setFormData({ name: '', status: 'active', position: '' });
      fetchClusters();
    } catch (error) {
      console.error('Add failed:', error);
    }
  };

  const handleEditSubmit = async () => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await putData(`category-cluster/update/${selectedCluster._id}`, formData, token);
      setOpenEditModal(false);
      setSelectedCluster(null);
      setFormData({ name: '', status: 'active', position: '' });
      fetchClusters();
    } catch (error) {
      console.error('Edit failed:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await deleteData(`category-cluster/delete/${selectedCluster._id}`, token);
      setOpenDeleteModal(false);
      setSelectedCluster(null);
      fetchClusters();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleStatusToggle = async (cluster) => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const newStatus = cluster.status === 'active' ? 'inactive' : 'active';
      await putData(`category-cluster/update/${cluster._id}`, { status: newStatus }, token);
      fetchClusters(); 
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  };

  const openEdit = (cluster) => {
    setSelectedCluster(cluster);
    setFormData({ name: cluster.name, status: cluster.status, position: cluster.position || '' });
    setOpenEditModal(true);
  };

  const openDelete = (cluster) => {
    setSelectedCluster(cluster);
    setOpenDeleteModal(true);
  };

  return (
    <Box p={3} sx={{ backgroundColor: '#f8fafc', minHeight: '100%', borderRadius: '15px' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 800, 
            color: '#1e293b',
            fontFamily: 'Mulish, sans-serif'
          }}>
            Category Cluster
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Manage group of categories for efficient warehouse allocation
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<MdAdd />}
          onClick={() => setOpenAddModal(true)}
          sx={{
            background: 'rgb(255 209 2)',
            color: 'black',
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 700,
            px: 3,
            py: 1,
            boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
            '&:hover': {
              background: 'rgb(255 190 0)',
              transform: 'translateY(-1px)'
            }
          }}
        >
          Add Cluster
        </Button>
      </Box>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by cluster name..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: '10px' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1); // Reset page on filter change
                }}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ 
        borderRadius: '12px', 
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#f1f5f9', fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase' } }}>
              <TableCell>Cluster Name</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton width="40%" /></TableCell>
                  <TableCell><Skeleton width="20%" /></TableCell>
                  <TableCell align="right"><Skeleton width="30%" /></TableCell>
                </TableRow>
              ))
            ) : clusters.length > 0 ? (
              clusters.map((cluster) => (
                <TableRow key={cluster._id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{cluster.name}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>{cluster.position || 0}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch 
                        checked={cluster.status === 'active'}
                        size="small"
                        onChange={() => handleStatusToggle(cluster)}
                        color="success"
                      />
                      <Chip
                        label={cluster.status}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          bgcolor: cluster.status === 'active' ? '#dcfce7' : '#f1f5f9',
                          color: cluster.status === 'active' ? '#166534' : '#475569',
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(cluster)} sx={{ color: '#006064', mr: 1 }}>
                      <MdEdit />
                    </IconButton>
                    <IconButton size="small" onClick={() => openDelete(cluster)} sx={{ color: '#ef4444' }}>
                      <MdDeleteForever />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box sx={{ py: 5, textAlign: 'center' }}>
                    <Typography color="textSecondary">No category clusters found</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          sx={{
            '& .MuiPaginationItem-root.Mui-selected': {
              bgcolor: 'rgb(255 209 2)',
              color: 'black',
              '&:hover': { bgcolor: 'rgb(255 190 0)' }
            }
          }}
        />
      </Box>

      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add New Cluster
          <IconButton onClick={() => setOpenAddModal(false)} size="small"><MdClose /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Cluster Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start"><FiType /></InputAdornment>
              }}
            />
            <TextField
              label="Position"
              type="number"
              fullWidth
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              helperText="Determines the display order on the Navbar"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiCircle color="#22c55e" size={14} /> Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiCircle color="#94a3b8" size={14} /> Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenAddModal(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddSubmit}
            disabled={!formData.name}
            sx={{ bgcolor: 'rgb(255 209 2)', color: 'black', '&:hover': { bgcolor: 'rgb(255 190 0)' } }}
          >
            Create Cluster
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Cluster
          <IconButton onClick={() => setOpenEditModal(false)} size="small"><MdClose /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Cluster Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start"><FiType /></InputAdornment>
              }}
            />
            <TextField
              label="Position"
              type="number"
              fullWidth
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              helperText="Determines the display order on the Navbar"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiCircle color="#22c55e" size={14} /> Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiCircle color="#94a3b8" size={14} /> Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenEditModal(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleEditSubmit}
            sx={{ bgcolor: 'rgb(255 209 2)', color: 'black', '&:hover': { bgcolor: 'rgb(255 190 0)' } }}
          >
            Update Cluster
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <DialogTitle>Delete Cluster</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the cluster "{selectedCluster?.name}"? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDeleteModal(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleDeleteConfirm} sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategoryCluster;
